/**
 * Bundle 78 Task 3 - 40px SVG sparkline showing the average rating
 * trend across the last 3 completed events. Mounts in the admin
 * event card header (AdminEventsListPage). Renders NOTHING when the
 * backend returns fewer than 2 points (avoids a flat single-dot
 * chart that would confuse the eye).
 *
 * No em/en dashes, no eslint-disable, no photo/image alt copy.
 */
import React, { useEffect, useState } from 'react';
import api from '@/utils/api';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

const RatingTrendSparkline = ({ n = 3, width = 96, height = 40 }) => {
  const [points, setPoints] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get('/admin/analytics/event-ratings/recent-trend', { params: { n } })
      .then((r) => { if (mounted) setPoints(r.data?.points || []); })
      .catch(() => { if (mounted) setPoints([]); });
    return () => { mounted = false; };
  }, [n]);

  if (!points || points.length < 2) return null;

  const values = points.map((p) => Number(p.avg) || 0);
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 5);
  const range = max - min || 1;
  const pad = 4;
  const stepX = points.length > 1 ? (width - 2 * pad) / (points.length - 1) : 0;

  const coords = values.map((v, i) => {
    const x = pad + i * stepX;
    const y = height - pad - ((v - min) / range) * (height - 2 * pad);
    return [x, y];
  });
  const path = coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c[0].toFixed(1)} ${c[1].toFixed(1)}`).join(' ');
  const last = values[values.length - 1];
  const first = values[0];
  const diff = last - first;
  const TrendIcon = diff > 0.1 ? TrendingUp : diff < -0.1 ? TrendingDown : Minus;
  const trendColor = diff > 0.1 ? 'text-emerald-600' : diff < -0.1 ? 'text-red-600' : 'text-neutral-500';

  return (
    <div className="flex items-center gap-2" data-testid="admin-rating-trend-sparkline">
      <svg width={width} height={height} className="overflow-visible" role="img" aria-label={`Rating trend across the last ${points.length} completed events, ending at ${last.toFixed(1)} of 5`}>
        <path d={path} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-secondary-600" />
        {coords.map(([x, y], i) => (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={i === coords.length - 1 ? 3 : 2}
            className={i === coords.length - 1 ? 'fill-secondary-700' : 'fill-secondary-400'}
            data-testid={`sparkline-point-${i}`}
          />
        ))}
      </svg>
      <div className={`flex items-center gap-1 text-xs ${trendColor}`} data-testid="sparkline-summary">
        <TrendIcon className="h-3 w-3" />
        <span className="font-mono">{last.toFixed(1)}</span>
        <span className="text-neutral-400 text-[10px]">/5 (last {points.length})</span>
      </div>
    </div>
  );
};

export default RatingTrendSparkline;
